import { FC, useState } from 'react'
import {
  Box, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select,
  Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material'
import { MdRefresh, MdSearch, MdViewColumn } from 'react-icons/md'
import { GetHCPFlowKanbanUseCase } from '../../../Usecase/HCP/Flows/get-hcp-flow-kanban-usecase'
import { ManageHCPFlowStatusUseCase } from '../../../Usecase/HCP/Flows/manage-hcp-flow-status-usecase'
import { MoveHCPFlowCardUseCase } from '../../../Usecase/HCP/Flows/move-hcp-flow-card-usecase'
import FlowManagementKanban from '../../../Components/HCP/FlowManagement/flow-management-kanban.component'
import FlowManagementCardDrawer from '../../../Components/HCP/FlowManagement/flow-management-card-drawer.component'
import FlowManagementStatusDialog from '../../../Components/HCP/FlowManagement/flow-management-status-dialog.component'
import { IFlowManagementCardSelection } from '../../../Services/HCP/flows/hcp-flow-card.interface'
import { HCPFlowEnum } from '../../../Services/HCP/flows/hcp-flow.interface'
import { canManageFlowColumns } from '../../../Services/HCP/flows/hcp-flow-status.helpers'
import { FiltroArteAprovadaType, useFlowManagement } from './flow-management.hook'

interface IFlowManagementProps {
  getHCPFlowKanbanUseCase: GetHCPFlowKanbanUseCase
  manageHCPFlowStatusUseCase: ManageHCPFlowStatusUseCase
  moveHCPFlowCardUseCase: MoveHCPFlowCardUseCase
}

const OPCOES_ARTE: { label: string; value: FiltroArteAprovadaType }[] = [
  { label: 'Todos', value: 'todos' },
  { label: 'Arte aprovada', value: 'aprovada' },
  { label: 'Arte pendente', value: 'pendente' },
]

const FlowManagement: FC<IFlowManagementProps> = ({
  getHCPFlowKanbanUseCase,
  manageHCPFlowStatusUseCase,
  moveHCPFlowCardUseCase,
}) => {
  const [selectedCard, setSelectedCard] = useState<IFlowManagementCardSelection | null>(null)
  const [statusDialogOpen, setStatusDialogOpen] = useState(false)

  const {
    activeFlow,
    flowTabs,
    columns,
    loading,
    loadingMoreColumnId,
    lastUpdatedLabel,
    filtroBusca,
    setFiltroBusca,
    filtroArte,
    setFiltroArte,
    handleTabChange,
    handleRefresh,
    handleLoadMoreColumn,
    handleCardMove,
    movingCardId,
  } = useFlowManagement({ getHCPFlowKanbanUseCase, moveHCPFlowCardUseCase })

  const activeTabIndex = Math.max(0, flowTabs.findIndex((tab) => tab.flow === activeFlow))
  const showArteFilter = activeFlow !== HCPFlowEnum.LOGISTICS
  const showColumnManager = canManageFlowColumns(activeFlow)

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 73px - 24px)',
        maxHeight: 'calc(100vh - 73px - 24px)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: '#f5f7fb',
          pt: 3,
          pb: 2,
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Gestão de Fluxo
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visão unificada dos fluxos operacionais do HCP
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {showColumnManager && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MdViewColumn />}
                  onClick={() => setStatusDialogOpen(true)}
                  disabled={loading}
                >
                  Gerenciar colunas
                </Button>
              )}
              <Button
              variant="outlined"
              size="small"
              startIcon={<MdRefresh />}
              onClick={handleRefresh}
              disabled={loading}
            >
              Atualizar
            </Button>
            </Box>
            {lastUpdatedLabel && (
              <Typography variant="caption" color="text.secondary">
                Atualizado em {lastUpdatedLabel}
              </Typography>
            )}
          </Box>
        </Box>

        <Tabs
          value={activeTabIndex}
          onChange={(_, index) => handleTabChange(flowTabs[index].flow)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 2,
            minHeight: 42,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 42,
            },
          }}
        >
          {flowTabs.map((tab) => (
            <Tab
              key={tab.flow}
              label={
                tab.enabled ? (
                  tab.label
                ) : (
                  <Tooltip title="Em breve">
                    <span>{tab.label}</span>
                  </Tooltip>
                )
              }
              disabled={!tab.enabled}
            />
          ))}
        </Tabs>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Pesquisar cartões..."
            value={filtroBusca}
            onChange={(event) => setFiltroBusca(event.target.value)}
            sx={{ minWidth: 280, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <MdSearch />
                </InputAdornment>
              ),
            }}
          />

          {showArteFilter && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Arte</InputLabel>
              <Select
                label="Arte"
                value={filtroArte}
                onChange={(event) => setFiltroArte(event.target.value as FiltroArteAprovadaType)}
              >
                {OPCOES_ARTE.map((opcao) => (
                  <MenuItem key={opcao.value} value={opcao.value}>
                    {opcao.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <FlowManagementKanban
          columns={columns}
          activeFlow={activeFlow}
          loading={loading}
          loadingMoreColumnId={loadingMoreColumnId}
          movingCardId={movingCardId}
          onLoadMoreColumn={handleLoadMoreColumn}
          onCardClick={setSelectedCard}
          onCardMove={handleCardMove}
        />
      </Box>

      <FlowManagementCardDrawer
        selection={selectedCard}
        onClose={() => setSelectedCard(null)}
      />

      {showColumnManager && (
        <FlowManagementStatusDialog
          open={statusDialogOpen}
          flow={activeFlow}
          manageHCPFlowStatusUseCase={manageHCPFlowStatusUseCase}
          onClose={() => setStatusDialogOpen(false)}
          onSaved={handleRefresh}
        />
      )}
    </Box>
  )
}

export default FlowManagement
